/**
 * useAudioNotification Hook
 * React hook for easy audio notification integration
 */

import { useEffect, useCallback, useRef } from 'react';
import audioNotificationManager from '../services/AudioNotificationManager';

/**
 * Hook for audio notifications
 * @param {object} options - Configuration options
 * @returns {object} Audio notification functions and state
 */
export const useAudioNotification = (options = {}) => {
  const {
    enabled = true,
    volume = 0.7,
    soundType = 'bell',
    autoPlay = false, // Auto-play on first alert
  } = options;

  const initialized = useRef(false);

  // Initialize audio manager on first render
  useEffect(() => {
    if (!initialized.current) {
      audioNotificationManager.initialize();
      audioNotificationManager.setEnabled(enabled);
      audioNotificationManager.setVolume(volume);
      initialized.current = true;
    }
  }, []);

  /**
   * Play notification sound
   */
  const playNotification = useCallback((customSoundType = soundType, customOptions = {}) => {
    audioNotificationManager.playSound(customSoundType, customOptions);
  }, [soundType]);

  /**
   * Stop notification sound
   */
  const stopNotification = useCallback(() => {
    audioNotificationManager.stopSound();
  }, []);

  /**
   * Update volume
   */
  const setVolume = useCallback((newVolume) => {
    audioNotificationManager.setVolume(newVolume);
  }, []);

  /**
   * Toggle sound on/off
   */
  const toggleSound = useCallback(() => {
    const settings = audioNotificationManager.getSettings();
    audioNotificationManager.setEnabled(!settings.enabled);
  }, []);

  /**
   * Enable/disable sound
   */
  const setSoundEnabled = useCallback((enabled) => {
    audioNotificationManager.setEnabled(enabled);
  }, []);

  /**
   * Test sound
   */
  const testSound = useCallback((testSoundType = soundType) => {
    audioNotificationManager.testSound(testSoundType);
  }, [soundType]);

  /**
   * Get current settings
   */
  const getSettings = useCallback(() => {
    return audioNotificationManager.getSettings();
  }, []);

  return {
    playNotification,
    stopNotification,
    setVolume,
    toggleSound,
    setSoundEnabled,
    testSound,
    getSettings,
  };
};

export default useAudioNotification;